import { TZDate } from '@date-fns/tz'
import {
  format,
  parseISO,
  formatDistance,
  differenceInMinutes,
  differenceInCalendarDays,
} from 'date-fns'
import { fr } from 'date-fns/locale'
import { DEFAULT_TIMEZONE } from '@/constants/geo'
import {
  DATETIME_PATTERN,
  DAY_PATTERN,
  formatInZoneOrNull,
} from './zoned-time.utils'

/**
 * The `Europe/Paris`-pinned layer, and the wall-clock convention that is being
 * removed. The rule itself — format an instant in an explicit zone — lives in
 * `zoned-time.utils.ts`; everything here either fills `DEFAULT_TIMEZONE` into
 * it, or handles a fake-`Z` field that has no zone to fill in.
 *
 * **This file gains no new callers.** Code that knows which site it is showing
 * calls the zoned pair with that site's resolved timezone (#1857 rule 4).
 *
 * The one exception is the "Calendar days" section below, which is not part of
 * the Paris layer and never was: `parseCalendarDay` / `formatCalendarDay` are
 * the two halves of rule 2 — a day carries no zone, so there is nothing for
 * either of them to convert and nothing for #1866 to delete. They live here
 * because `franceTodayDate` is the one day-shaped value that DOES need Paris,
 * and splitting the three across two files is how a reader finds the wrong one.
 *
 * The `datetime-local` writers are gone (#2075): they were Paris-pinned, and an
 * exported one is how the skew comes back — the next person needing a picker
 * value finds it before they find `zoned-time.utils.ts`.
 *
 * **No writer of any shape is left** (#2087). `parseToFranceISO` and
 * `parseStringToDate`'s wall-clock branch were an inverse pair that agreed only
 * with each other — a `Date`'s BROWSER-local fields out, the same digits read
 * back as browser-local in — and they survived only while the intervention write
 * endpoints refused an instant. #2065 made them take one, so every write path
 * now converts against the SITE's zone and both are deleted. What survives, and
 * why, each with the issue that removes it:
 *
 * - `formatBusinessDateTime` — two readers left, and neither is a column: the
 *   activity timeline (#2071) and the bell's `metadata.scheduledAt`
 *   (`notification-rich-content.tsx`), both STORED JSONB payloads that name no
 *   zone and outlive the code that wrote them. The « À faire » invoice cells
 *   and `intervention-passage.ts`'s legacy arm left with the columns (#1862);
 *   the agenda left with its drag writer (#2087).
 *
 *   `date-utils.formatDate` was the fourth, and is GONE (#2072 — the
 *   calendar-day half in #2107, the rest in #2108). It was one call serving
 *   ~24 readers across six domains, so it told every one of them the value is
 *   a Paris wall clock whatever column it came from; `date-utils.ts` records
 *   which helper replaced it for which kind of value.
 * - `formatFranceTime` and the relative/day helpers — Paris wrappers with no
 *   site in scope. They are rule-4 debt, not wall-clock debt.
 *
 * @see docs/standards/datetime-strategy.md
 */

/**
 * Parses an ISO 8601 string to a `Date`.
 *
 * It used to take the legacy `"YYYY-MM-DD HH:mm:ss"` wall clock too, reading
 * those digits as the BROWSER's local time — the exact inverse of what
 * `parseToFranceISO` wrote, which is why the two only ever agreed with each
 * other. Both left with the write paths (#2087): nothing produces those digits
 * for this to read back, and a reader that meets a STORED one wants
 * `formatBusinessDateTime`, which prints those Paris digits verbatim — never
 * the browser's zone.
 *
 * Prefer {@link formatBusinessDateTime} or {@link formatFranceTime} over
 * calling this directly on API response data.
 */
export function parseStringToDate(dateString: string): Date {
  return parseISO(dateString)
}

// ---------------------------------------------------------------------------
// Business datetime formatting (wall-clock / Paris local time)
// ---------------------------------------------------------------------------

/**
 * Formats a **Paris wall clock** serialized with a trailing "Z" (JavaScript's
 * Date.toJSON() artifact — the value is NOT actual UTC). This function strips
 * "Z" before parsing, so the displayed time always matches the stored digits.
 *
 * **When to use:** only a STORED payload that still holds such a value and
 * names no zone — the activity log's metadata, and the bell's
 * `metadata.scheduledAt`. #1862 dropped the `businessTimestamp()` columns this
 * was written for, so no INTERVENTION field is one of these any more: read the
 * `*At` instant in the site's zone instead (`intervention-passage.ts`).
 *
 * @example
 * ```ts
 * formatBusinessDateTime(metadata.scheduledAt)
 * // "15/03/2026 à 14:30"
 *
 * formatBusinessDateTime(metadata.scheduledAt, "dd/MM/yyyy")
 * // "15/03/2026"
 * ```
 *
 * @param date - ISO string (with or without "Z") or Date object from API
 * @param formatStr - date-fns format string (default: "dd/MM/yyyy 'à' HH:mm")
 * @returns Formatted string, or empty string if date is falsy
 *
 * @see {@link formatFranceTime} for system timestamps (actual UTC values)
 * @see docs/standards/datetime-strategy.md
 */
export function formatBusinessDateTime(
  date: string | Date | null,
  formatStr = "dd/MM/yyyy 'à' HH:mm"
): string {
  if (!date) return ''

  try {
    let dateObj: Date

    if (typeof date === 'string') {
      // Strip Z suffix — database stores wallclock (Paris time), not actual UTC.
      // The Z is added by JavaScript's Date.toJSON() during API serialization.
      let dateStr = date
      if (dateStr.endsWith('Z')) {
        dateStr = dateStr.slice(0, -1)
      }
      dateObj = parseISO(dateStr)
    } else {
      dateObj = date
    }

    return format(dateObj, formatStr, { locale: fr })
  } catch {
    return String(date)
  }
}

// ---------------------------------------------------------------------------
// System datetime formatting (actual UTC → Europe/Paris)
// ---------------------------------------------------------------------------

/**
 * Formats **system timestamps** that represent actual UTC moments
 * (e.g., createdAt, updatedAt, sentAt, paidAt, readAt).
 *
 * Converts the UTC value to Europe/Paris timezone before formatting,
 * so the displayed time reflects Paris wall-clock time at that moment.
 *
 * **When to use:** Any date field backed by `timestamp with time zone`
 * or `auditTimestamps`: `createdAt`, `updatedAt`, `deletedAt`, `sentAt`,
 * `paidAt`, `assignedAt`, `readAt`, `signedAt`, etc.
 *
 * **Do NOT use** for business timestamps — use {@link formatBusinessDateTime}.
 *
 * Paris is this function's whole content: it is {@link formatInZone} with
 * `DEFAULT_TIMEZONE` filled in. A surface that knows which site it is showing
 * calls `formatInZone` with that site's resolved zone instead (#1857 rule 4),
 * and this wrapper goes away with the last caller that does not (#1866).
 *
 * @example
 * ```ts
 * formatFranceTime(notification.createdAt)
 * // "15/03/2026 à 14:30"
 *
 * formatFranceTime(invoice.sentAt, 'HH:mm')
 * // "14:30"
 * ```
 *
 * @param date - ISO string with "Z" or Date object
 * @param formatStr - date-fns format string (default: "dd/MM/yyyy 'à' HH:mm")
 * @returns Formatted string, or empty string if date is falsy
 *
 * @see {@link formatBusinessDateTime} for business timestamps (wall-clock)
 * @see docs/standards/datetime-strategy.md
 */
export function formatFranceTime(
  date: string | Date | null,
  formatStr = DATETIME_PATTERN
): string {
  if (!date) return ''

  // Echo the ORIGINAL input on failure — not the `Invalid Date` that parsing it
  // produced, and not a `Date`'s English browser-zone `toString()` in a French
  // UI. Hence `formatInZoneOrNull` and a fallback here rather than
  // `formatInZone`, which can only echo what IT was handed. The try/catch stays
  // because this sits in ~20 render paths and the declared type is not a
  // guarantee: an API field arriving as a number or an object must show its raw
  // value, not take the React subtree down.
  try {
    const dateObj = typeof date === 'string' ? parseStringToDate(date) : date
    return (
      formatInZoneOrNull(dateObj, DEFAULT_TIMEZONE, formatStr) ?? String(date)
    )
  } catch {
    return String(date)
  }
}

// ---------------------------------------------------------------------------
// Calendar days (date-only values)
// ---------------------------------------------------------------------------

/**
 * A `businessDate()` value — `YYYY-MM-DD`, or a wider string that starts with
 * one (the same day stamped with a time by `Date.toJSON()`).
 */
const CALENDAR_DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/

/**
 * A stored calendar day → the `Date` a picker can select, read in the
 * BROWSER's own zone (#1904).
 *
 * `new Date('2026-09-15')` is UTC midnight, not a day: west of London that
 * instant's local fields say **14** September, so a picker fed it highlighted
 * the day before the one the row holds. The bug is structurally invisible from
 * Paris, and stayed hidden because the two halves of the same component
 * disagreed — the trigger label goes through `parseISO`, which reads a
 * date-only string as LOCAL midnight and therefore printed the right day next
 * to a calendar showing the wrong one.
 *
 * A calendar day carries no zone (epic #1857 rule 2), so it is not converted:
 * the three fields are read verbatim and re-stamped local, which is the exact
 * inverse of the `format(date, 'yyyy-MM-dd')` every picker writes back. That
 * symmetry is the point — the value a picker reads back is the one it wrote.
 *
 * Do NOT "fix" this to resolve the day in the SITE's zone. #1857's rule that a
 * day belongs to the site governs which day a moment falls on, and the value
 * here has already been reduced to a day — there is no moment left to place.
 * react-day-picker compares a `Date`'s LOCAL fields, so a day built in any
 * other zone can land the highlight on a neighbouring cell, which is the bug
 * this function exists to remove. Deriving a day from an instant, or a range
 * bound from a day, is the site-zone case and belongs in the readers (#1865),
 * not here.
 *
 * Safe across a DST-at-midnight zone (Santiago, Havana, Beirut): where local
 * 00:00 does not exist, `new Date(y, m, d)` lands on 01:00 the SAME day, so
 * the calendar day is preserved and the round-trip guard below still passes.
 *
 * @param value - `YYYY-MM-DD` (or a `Date`, passed through) from the API or
 *                from form state
 * @returns the local `Date` for that day, or `undefined` when there is nothing
 *          selectable — react-day-picker's `selected` and `before` both take
 *          `undefined` for "none", so callers need no fallback of their own
 */
export function parseCalendarDay(
  value: string | Date | null | undefined
): Date | undefined {
  if (!value) return undefined

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : value
  }

  const match = CALENDAR_DAY_PATTERN.exec(value)
  if (!match) return undefined

  const year = Number(match[1])
  const monthIndex = Number(match[2]) - 1
  const day = Number(match[3])
  const parsed = new Date(year, monthIndex, day)

  // `new Date(y, m, d)` rolls out-of-range parts over silently (month 13 is
  // next January) and maps a 2-digit year into the 1900s, so a malformed value
  // would come back as a plausible wrong day. Read the fields back instead of
  // trusting the constructor: anything that did not survive the round trip was
  // never a real calendar day.
  const isExactDay =
    parsed.getFullYear() === year &&
    parsed.getMonth() === monthIndex &&
    parsed.getDate() === day

  return isExactDay ? parsed : undefined
}

/**
 * A stored calendar day → the day a French reader sees, `dd/MM/yyyy`.
 *
 * The read half of {@link parseCalendarDay}, and the only formatter a `date()`
 * column — or a day a `DatePicker` wrote — may go through. It is deliberately
 * zone-free, and takes no `timeZone` parameter to be zone-free WITH: a calendar
 * day carries no zone (#1857 rule 2), so there is no moment left in it to
 * place. Converting one is how « 15/09 » becomes « 14/09 » for a reader
 * standing west of wherever the conversion decided to start.
 *
 * It replaced `date-utils.formatDate` at these call sites (#2107). That helper
 * was `formatBusinessDateTime`, whose job is to STRIP a fake `Z` and print the
 * digits — it happened to give the right answer for a bare `YYYY-MM-DD`, by a
 * route that asserts the value is a Paris wall clock. `date-picker.tsx` showed
 * what that costs: it formatted its trigger label through that helper while the
 * calendar beside it used `parseCalendarDay`, so the two halves of one widget
 * read the same string by different rules — the disagreement that hid #1904.
 *
 * A wider value is truncated to its leading day, which is what the technician
 * vacation window needs: `unavailable_from_at` holds midnight on the day a
 * picker wrote, and the view shows a day.
 *
 * @param value - `YYYY-MM-DD`, a wider string starting with one, or the `Date`
 *                {@link parseCalendarDay} hands back
 * @returns the formatted day, `''` when there is nothing to show, or the input
 *          echoed back when it is not a calendar day at all — same contract as
 *          `formatInZone`, and for the same reason: a formatter sitting in a
 *          table cell must not blank a value it cannot read, because a raw
 *          string tells an operator something is wrong and an empty cell does
 *          not
 */
export function formatCalendarDay(
  value: string | Date | null | undefined
): string {
  if (!value) return ''

  const day = parseCalendarDay(value)
  if (!day) return String(value)

  return format(day, DAY_PATTERN, { locale: fr })
}

// ---------------------------------------------------------------------------
// Conversion helpers (frontend → API)
// ---------------------------------------------------------------------------

/**
 * The current instant, reading its fields (`getHours`, `getDate`, …) in
 * Europe/Paris.
 *
 * A `TZDate`, not `date-fns-tz`'s `toZonedTime`: the latter shifts the instant
 * and rebuilds Paris's wall clock as a browser-local `Date`, which rolls an
 * hour forward whenever it falls in the BROWSER's own spring-forward gap
 * (#2413). Every helper below reads Paris fields or compares instants, so a
 * `TZDate` is a drop-in for both.
 */
function getFranceNow(): Date {
  const now = new Date()
  return new TZDate(now, DEFAULT_TIMEZONE)
}

/**
 * Today in Europe/Paris as a calendar day (#1920): local midnight on the PARIS
 * day, the shape {@link parseCalendarDay} returns, so a reader formatting it in
 * its own zone still prints the Paris day.
 *
 * Use this wherever a surface has to derive "today" as a business day.
 * `new Date().toISOString().split('T')[0]` takes the UTC day, which is already
 * a day behind between 00:00 and 02:00 in Paris itself. `getFranceNow()` keeps
 * a time-of-day, so it is an instant and can still roll back west of London.
 */
export function franceTodayDate(): Date {
  const paris = getFranceNow()
  return new Date(paris.getFullYear(), paris.getMonth(), paris.getDate())
}

// ---------------------------------------------------------------------------
// Relative / comparison helpers
// ---------------------------------------------------------------------------

/**
 * Formats a system timestamp as a relative time string in French
 * (e.g., "il y a 5 minutes"). Compares against current Paris time.
 */
export function formatRelativeToFrance(date: string | Date): string {
  if (!date) return ''

  try {
    const dateObj = typeof date === 'string' ? parseStringToDate(date) : date
    // Convert to Europe/Paris timezone for consistent comparison
    const parisTime = new TZDate(dateObj, DEFAULT_TIMEZONE)
    const franceNow = getFranceNow()

    return formatDistance(parisTime, franceNow, {
      addSuffix: true,
      locale: fr,
    })
  } catch {
    return String(date)
  }
}

/**
 * Returns true if the given system timestamp is in the past
 * relative to current Paris time.
 */
export function isPastFranceTime(date: string | Date): boolean {
  if (!date) return false

  try {
    const dateObj = typeof date === 'string' ? parseStringToDate(date) : date
    // Convert to Europe/Paris timezone for consistent comparison
    const parisTime = new TZDate(dateObj, DEFAULT_TIMEZONE)
    const franceNow = getFranceNow()
    return parisTime < franceNow
  } catch {
    return false
  }
}

/**
 * Returns true if the given system timestamp falls on today's date
 * in the Europe/Paris timezone.
 */
export function isTodayFrance(date: string | Date): boolean {
  if (!date) return false

  try {
    const dateObj = typeof date === 'string' ? parseStringToDate(date) : date
    // Convert to Europe/Paris timezone for consistent comparison
    const parisTime = new TZDate(dateObj, DEFAULT_TIMEZONE)
    const franceNow = getFranceNow()

    const dateOnly = new Date(
      parisTime.getFullYear(),
      parisTime.getMonth(),
      parisTime.getDate()
    )
    const franceToday = new Date(
      franceNow.getFullYear(),
      franceNow.getMonth(),
      franceNow.getDate()
    )

    return dateOnly.getTime() === franceToday.getTime()
  } catch {
    return false
  }
}

/**
 * Returns true if the given system timestamp falls on yesterday's date
 * in the Europe/Paris timezone.
 */
export function isYesterdayFrance(date: string | Date): boolean {
  if (!date) return false

  try {
    const dateObj = typeof date === 'string' ? parseStringToDate(date) : date
    const parisTime = new TZDate(dateObj, DEFAULT_TIMEZONE)
    const franceNow = getFranceNow()

    const dateOnly = new Date(
      parisTime.getFullYear(),
      parisTime.getMonth(),
      parisTime.getDate()
    )
    const franceYesterday = new Date(
      franceNow.getFullYear(),
      franceNow.getMonth(),
      franceNow.getDate() - 1
    )

    return dateOnly.getTime() === franceYesterday.getTime()
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// Notification formatting
// ---------------------------------------------------------------------------

/**
 * Formats a system timestamp as compact French relative time for notifications.
 *
 * - < 1 minute: "A l'instant"
 * - Today: "Aujourd'hui a HH:mm"
 * - Yesterday: "Hier a HH:mm"
 * - < 7 days: "Il y a X jours"
 * - Older: "Le DD/MM/YYYY a HH:mm"
 *
 * @param date - System timestamp (actual UTC) from notification createdAt
 */
export function formatNotificationTime(date: string | Date): string {
  if (!date) return ''

  try {
    const dateObj = typeof date === 'string' ? parseStringToDate(date) : date
    const parisTime = new TZDate(dateObj, DEFAULT_TIMEZONE)
    const now = new TZDate(new Date(), DEFAULT_TIMEZONE)

    const days = differenceInCalendarDays(now, parisTime)

    if (days === 0) {
      const minutes = differenceInMinutes(now, parisTime)
      if (minutes < 1) return "À l'instant"
      return `Aujourd'hui à ${format(parisTime, 'HH:mm', { locale: fr })}`
    }

    if (days === 1)
      return `Hier à ${format(parisTime, 'HH:mm', { locale: fr })}`
    if (days < 7) return `Il y a ${days} jours`
    return `Le ${format(parisTime, "dd/MM/yyyy 'à' HH:mm", { locale: fr })}`
  } catch {
    return String(date)
  }
}
